import { InputAdornment, TextField } from "@mui/material";
import {
  CoverContainer,
  CoverContent,
  CoverImage,
  SearchWrapper,
  SearchButton,
} from "./styles";
import coverImg from "../../../../assets/icons/homepage_logo.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import i18n from "i18next";
import {
  useFieldsOfStudyQuery,
  useLanguageSkillQuery,
  useLanguagesQuery,
  useProfessionsQuery,
  useUniversityQuery,
} from "../../../../rq/hooks/useQueries";
import { OptionType } from "../../../../interfaces/types/Option.type";
import { professions } from "../../../../lib/constants";

interface SearchProps {
  searchWord: string;
  search: any;
  setSearch: (search: any) => void;
  setSearchWord: (searchWord: string) => void;
}

interface NamedItem {
  id: number;
  name: string;
}

export default function CoverSection({
  setSearchWord,
  setSearch,
  searchWord,
}: SearchProps) {
  const navigate = useNavigate();
  const [isTranslating, setIsTranslating] = useState(false);
  const { t } = useTranslation();

  const handleChange = (e: any) => {
    setSearchWord(e.target.value);
  };

  // const { data: professionsList = [] } = useProfessionsQuery();
  // const { data: languages = [] } = useLanguagesQuery();
  // const { data: languageSkillLevels = [] } = useLanguageSkillQuery();
  const { data: educations = [] } = useUniversityQuery();
  // const { data: studyFields = [] } = useFieldsOfStudyQuery();

  console.log(
    "pr",
    educations
      ?.map((list: OptionType) => ({
        id: list.id,
        name: list.name,
      }))
      .sort((a: NamedItem, b: NamedItem) => a.name.localeCompare(b.name)) || []
  );

  /**
   * Translates text from any language to English using Google Cloud Translation API
   */
  const handleTranslation = async (text: string): Promise<string> => {
    if (!text.trim()) return text;

    try {
      // Clean input text - remove extra spaces, lowercase
      const cleanedText = text.toLowerCase().trim();

      // Minimum similarity threshold (0-1, where 1 is exact match)
      const SIMILARITY_THRESHOLD = 0.8;

      // Calculate string similarity using Levenshtein distance
      const calculateSimilarity = (a: string, b: string): number => {
        if (a.length === 0) return b.length;
        if (b.length === 0) return a.length;

        // Simple implementation of Levenshtein distance
        const matrix: number[][] = [];

        // Initialize matrix
        for (let i = 0; i <= b.length; i++) {
          matrix[i] = [i];
        }

        for (let j = 0; j <= a.length; j++) {
          matrix[0][j] = j;
        }

        // Fill matrix
        for (let i = 1; i <= b.length; i++) {
          for (let j = 1; j <= a.length; j++) {
            const cost = a[j - 1] === b[i - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
              matrix[i - 1][j] + 1, // deletion
              matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j - 1] + cost // substitution
            );
          }
        }

        // Calculate similarity score (0-1)
        const distance = matrix[b.length][a.length];
        const maxLength = Math.max(a.length, b.length);
        return 1 - distance / maxLength;
      };

      // Find best matching profession
      let bestMatch = {
        profession: null as any,
        similarity: 0,
        language: "",
      };

      // Check French professions
      for (const prof of professions.french) {
        const similarity = calculateSimilarity(
          cleanedText,
          prof.name.toLowerCase()
        );
        if (similarity > bestMatch.similarity) {
          bestMatch = { profession: prof, similarity, language: "french" };
        }
      }

      // Check Dutch professions
      for (const prof of professions.dutch) {
        const similarity = calculateSimilarity(
          cleanedText,
          prof.name.toLowerCase()
        );
        if (similarity > bestMatch.similarity) {
          bestMatch = { profession: prof, similarity, language: "dutch" };
        }
      }

      // If we found a match with sufficient similarity
      if (
        bestMatch.profession &&
        bestMatch.similarity >= SIMILARITY_THRESHOLD
      ) {
        // Find the English equivalent using the ID
        const englishEquivalent = professions.english.find(
          (prof) => prof.id === bestMatch.profession.id
        );

        if (englishEquivalent) {
          console.log(
            `Found fuzzy match: "${text}" → "${bestMatch.profession.name}" (${
              bestMatch.language
            }) → "${
              englishEquivalent.name
            }" (Similarity: ${bestMatch.similarity.toFixed(2)})`
          );
          return englishEquivalent.name;
        }
      } else if (bestMatch.profession) {
        console.log(
          `Found potential match but below threshold: "${text}" → "${
            bestMatch.profession.name
          }" (Similarity: ${bestMatch.similarity.toFixed(2)})`
        );
      } else {
        console.log(`No profession match found for "${text}" in any language`);
      }

      // If no match found in professions, return the original text
      return text;
    } catch (error) {
      console.error("Translation attempt failed:", error);
      return text;
    }
  };
  // ...existing code...

  const handleSearch = async () => {
    if (!searchWord.trim()) return;

    try {
      setIsTranslating(true);
      const translatedSearchWord = await handleTranslation(searchWord);

      console.log(
        `Original: "${searchWord}", Processed: "${translatedSearchWord}"`
      );
      setSearch((prev: any) => !prev);

      // Use the translated word for the API call, but keep the original searchWord in state
      // Add a separate parameter for the original text to maintain it in the UI
      navigate(
        `/candidate/search?searchWord=${encodeURIComponent(
          translatedSearchWord
        )}&originalInput=${encodeURIComponent(searchWord)}`
      );
    } catch (error) {
      console.error("Search error:", error);
      navigate(
        `/candidate/search?searchWord=${encodeURIComponent(searchWord)}`
      );
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <CoverContainer>
      <CoverContent>
        <CoverImage src={coverImg} loading="lazy" />
        <SearchWrapper>
          <TextField
            size="small"
            fullWidth
            placeholder={t("search_for_student")}
            id="input-with-icon-textfield"
            sx={{
              backgroundColor: "white",
              borderRadius: "10px",
              input: {
                padding: 0,
              },
            }}
            className="py-2.5 px-1 pl-2"
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchButton onClick={handleSearch} className="rounded-lg">
                    {t("card.search.title")}
                  </SearchButton>
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            variant="standard"
          />
        </SearchWrapper>
      </CoverContent>
    </CoverContainer>
  );
}
